import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import {
  AccountEntities,
  EntityTypeID,
  ErrorTypes,
  FeatureFlags,
  FieldListObject,
  Filter,
  SeverityLabels,
  useStaticErrorSnackBar,
  WorkflowType,
} from '@fabric-ui/components';
import { emptyArray, emptyObject, isDeepEqual, noop, QueryKey } from '@fabric-ui/utils';
import { generateSourcesValuesQuery } from '@shared/Tickets/utils';
import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { useMatch } from '@tanstack/react-router';
import { useLocation } from '@utils/AvRouter';
import { EntityProjectionName, entityProjectionNames } from '@utils/entityProjectionNames';
import { getRelatedEntityQueryConfig } from '@utils/entityRelationshipQueryConfig';
import { ACTIONS_MAP, entityViewConfig, getActionsPermissionConfig } from '@utils/entityViewConfig';
import { and, field } from '@utils/filter';
import { isEmptyObject } from '@utils/filterUtils';
import { appToPermissionCategory, projectionNameToCategoryName } from '@utils/permissions.utils';
import { getAppIdFromPathName } from '@utils/router.utils';
import {
  arrayToCsv,
  downloadBlob,
  formatStartEachWordWithCapitalLetter,
  getEscapedValue,
  getFieldName,
  isNullOrUndefined,
  sqlFormatter,
} from '@utils/utils';
import { Outegration, TARGET_MATCHING_CATEGORY_ENUM, TARGET_MATCHING_TYPE_ENUM } from '@views/Outegrations/types';
import { add, sub } from 'date-fns';
import { gql } from 'graphql-request';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { MessageStatus, NotificationContext } from '../../context/AvSnackBarProvider';
import useQueryObject from '../../hooks/useQueryObjectSql';
import useQuerySql, { queryOrderBy } from '../../hooks/useQuerySql';
import { Permission, PermissionAppName, PermissionCategoriesNames, PermissionEntitiesNames } from '../../types';
import { ScreenType } from '../../types/savedViews.types';
import { DwQueryRequest, QueryObject } from '../CustomDashboards/types';
import { getIsManualOverrideEnabled, useFieldRegulationsData } from '../ModelManagement/hooks';
import { useGetBucketAndStatuses } from '../Settings/TicketStatuses/hooks';
import { TabTypeEnum, TTab } from '../Settings/UiConfigSettings/types';
import { FieldType } from '../Sources/Mapping/types';
import { useCurrentEntityViewConfig } from './Threats/entityViewAppContext';
import { apiUrls, formatAsUTC, groupByKeys, groupByKeysQueryProjection, TABLE_AVALOR_KEY } from './ticket.types';
import { ConfigField, getFieldId, getMetricsByProjName } from './ticketInfo.utils';
import { hasPermissionToReadTicketOutegrations } from './utils';

interface ExceptionRulesFilterResponse {
  scope: string;
  filter: Filter | null;
}

export const useConfirmedAndRemediatedBuckets = proj => {
  const { buckets } = useGetBucketAndStatuses(proj);
  return useMemo(() => {
    const confirmedBucket = buckets.find(b => b.closed) || { statuses: [] };
    const remediatedBucket = buckets.find(b => b.remediated) || { statuses: [] };

    return { confirmedBucket, remediatedBucket };
  }, [buckets]);
};

interface CreateDestinationIssueForTicketOptions {
  entityKey: string | undefined | number;
  projectionId: EntityTypeID;
  onSuccess?: (res: any) => void;
}

interface ActionStatus {
  mode: 'loading' | 'success' | 'error';
  message?: string;
  timedOut?: boolean;
}

export interface IOutegration {
  id: string;
  name: string;
  active: boolean;
  integrationType: string;
}

export type CreateIssueParams = {
  integrationId: string;
  integrationKey?: string;
  dependency?: Promise<any>;
  destinationName: string;
};

export type IntegrationSingleTM = {
  entityKeyToIntegrationId: EntityKeyToIntegrationId[];
  projectionId: EntityTypeID;
};

export type EntityKeyToIntegrationId = {
  entityKey: string;
  integrationId: string;
};

export const useCreateDestinationIssueForTicket = ({
  entityKey,
  projectionId,
  onSuccess = noop,
}: CreateDestinationIssueForTicketOptions) => {
  const { api } = useAvContext();
  const staticErrorSnackBar = useStaticErrorSnackBar();
  const { setRunInfo } = useContext(NotificationContext);

  const [actionStatus, setActionStatus] = useState<Partial<ActionStatus>>({});

  const createIssue = useCallback(
    ({ integrationId, integrationKey, dependency = Promise.resolve(), destinationName }: CreateIssueParams) => {
      setActionStatus({ mode: 'loading' });
      const requestUrl = integrationKey ? `${apiUrls.CREATE_SINGLE_INTEGRATION}/manual` : apiUrls.CREATE_SINGLE_INTEGRATION;
      return dependency
        .then(() =>
          api(requestUrl, {
            options: { method: 'POST' },
            onError: () => {},
            isWebserver: false,
            body: { entityKey, integrationId, integrationKey, projectionId },
            onSuccess: (res: any) => {
              onSuccess(res);
              if (res.attachmentWfResponse) {
                setRunInfo({
                  wfId: res.attachmentWfResponse.workflowId,
                  runId: res.attachmentWfResponse.runId,
                  workflowType: WorkflowType.IntegrationAttachment,
                });
              }
              setActionStatus({ mode: 'success' });
            },
          })
        )
        .catch((e: Error) => {
          setActionStatus({ mode: 'error', message: e.message });
          staticErrorSnackBar({ title: `Create ${destinationName} Ticket Failed`, message: e.message });
        })
        .finally(() => setTimeout(() => setActionStatus(prev => ({ ...prev, timedOut: true })), 3000));
    },
    [entityKey, onSuccess]
  );

  return { createIssue, actionStatus };
};

const mapValueFunc = v => {
  const stringValue = `${v}`;
  return `'${getEscapedValue(stringValue)}'`;
};
export const PAGE_SIZE = 20;
const ROW_LIMIT = 40000;

const aliasRegex = /(?: as )/gi;

const getDefaultOrderBy = f => {
  const field = (f && f.split(aliasRegex)?.[1]) || f;
  return [{ property: field }];
};

export const useValidateEntitiesExist = ({
  mainAlias,
  enabled,
  entityKeys = emptyArray,
  projectionName,
}: {
  enabled: boolean;
  entityKeys: string[];
  mainAlias: string;
  projectionName: string;
}) => {
  const keyField = `${mainAlias}._key`;
  const queryObject: QueryObject = {
    select: {
      dims: [{ name: keyField }],
      metrics: [],
    },
    filter: and(field(keyField).str.in(entityKeys)),
    projectionId: { name: projectionName, builtIn: true },
    sorting: [],
  };
  const { data = emptyArray, isLoading } = useQueryObject({
    queryObject,
    onSuccess: d => d.map(v => v[keyField]),
    options: { enabled },
  });
  const existingEntities = entityKeys.filter(key => data.includes(key));
  return { existingEntities, isLoading };
};

export const useTicketFieldsQuery = ({
  ticketKey,
  queryKey = QueryKey.TabTable,
  where = '',
  page = 0,
  fields = [],
  fieldToExcludeFromExport = [],
  orderBy = getDefaultOrderBy(fields[0]),
  enabled,
  keyPrefix = 'finding',
  groupBy,
  parentEntityPathAlias = 'ticket',
  isCountDistinctValues,
  projectionName,
  isDistinct,
  skipRelatedEntityConfig = false,
}: {
  ticketKey: string;
  queryKey?: string;
  where?: string;
  page?: number;
  fields?: string[];
  fieldToExcludeFromExport?: string[];
  orderBy?: any[];
  enabled?: boolean;
  keyPrefix?: string;
  groupBy?: string[];
  parentEntityPathAlias?: string;
  isCountDistinctValues?: boolean;
  projectionName: string;
  isDistinct?: boolean;
  skipRelatedEntityConfig?: boolean;
}) => {
  const {
    measurements,
    userPermissions: { isInternalRole },
  } = useAvContext();
  const { enqueueSnackbar } = useSnackbar();
  const shouldCountDistinctValues = isCountDistinctValues;

  const relatedEntityConfig = skipRelatedEntityConfig ? null : getRelatedEntityQueryConfig(projectionName);
  const fromTable = relatedEntityConfig?.targetTable || projectionName;
  const effectiveKeyPrefix = relatedEntityConfig?.keyPrefix || keyPrefix;

  const keyField = relatedEntityConfig?.keyField ?? '_key';
  const relationshipCondition = relatedEntityConfig
    ? relatedEntityConfig.relatedProjName
      ? `${relatedEntityConfig.relationshipField}.${keyField} = ${mapValueFunc(ticketKey)} AND ${relatedEntityConfig.relationshipField}.related_proj_name = ${mapValueFunc(relatedEntityConfig.relatedProjName)}`
      : `${relatedEntityConfig.relationshipField}.${keyField} = ${mapValueFunc(ticketKey)}`
    : `${parentEntityPathAlias}._key = ${mapValueFunc(ticketKey)}`;

  const whereSQL = `${where ? `${where} AND` : 'WHERE'} ${relationshipCondition}`;

  const metricsIncludedInFields = useMemo(
    () =>
      measurements.visible.reduce((acc: Set<string>, metric: any) => {
        if (fields.includes(metric.systemName)) {
          acc.add(metric.systemName);
        }

        return acc;
      }, new Set()),
    [fields, measurements]
  );

  const fieldsWithoutMetrics = fields.filter((field: string) => !metricsIncludedInFields.has(field));
  const isAggregateField = (f: string) => /^(COLLECT_DISTINCT|COLLECT|MAX|MIN|SUM|COUNT|AVG)\(/i.test(f);
  const groupByFields = fieldsWithoutMetrics.filter(f => !isAggregateField(f)).map(field => getFieldId(field));

  const allSQL = `SELECT ${isDistinct ? 'DISTINCT ' : ''}${[
    ...new Set([
      ...fieldsWithoutMetrics,
      ...Array.from(metricsIncludedInFields).map((systemName: string) => `${systemName} as ${systemName}`),
      ...(groupBy ? [] : [`${effectiveKeyPrefix}._key as ${TABLE_AVALOR_KEY}`]),
    ]),
  ].join(',')}
  FROM ${fromTable} ${whereSQL}
      GROUP BY ${groupBy ? groupBy.join(', ') : groupByFields.join(', ')}
      ${queryOrderBy([...orderBy, ...(groupBy ? [] : [{ property: TABLE_AVALOR_KEY, isAsc: true }])])}`;

  const exportSql = fieldToExcludeFromExport.length
    ? `select ${isDistinct ? 'DISTINCT ' : ''}${[...new Set([...fields.filter((f: string) => !fieldToExcludeFromExport.includes(f)), `${effectiveKeyPrefix}._key`])].join(',')}
       FROM ${fromTable} ${whereSQL}
           ${groupBy ? `GROUP BY ${groupBy.join(', ')}` : ''}
           ${queryOrderBy([...orderBy, ...(groupBy ? [] : [{ property: `${effectiveKeyPrefix}._key`, isAsc: true }])])}`
    : allSQL;

  const sql = `AVALOR PARAM total_row_count = true;  ${allSQL} OFFSET ${page * PAGE_SIZE} LIMIT ${PAGE_SIZE}`;

  const onError = isInternalRole
    ? (e: any) => {
        const error = e?.[0]?.message || e?.message;
        return error && enqueueSnackbar(`${ErrorTypes.Setup}: ${error}`, { variant: 'error' });
      }
    : undefined;

  const { data, isLoading, isRefetching, totals, errors } = useQuerySql({
    key: queryKey,
    sql,
    options: { enabled: enabled && !!ticketKey },
    muteErrors: true,
    debounceQueries: false,
    onError,
    showTotals: true,
  });

  const hasNonNullPrefix = 'HAS_NON_NULL_VALUES_';
  const distinctCountWhereSQL = `WHERE ${relationshipCondition}`;
  const populatedFieldsQuery =
    shouldCountDistinctValues &&
    `SELECT ${fields
      .filter((f: string) => !f.match(aliasRegex))
      .map((f: string) => `MAX(IF(${f} != null, 1, 0)) as "${hasNonNullPrefix}${f}"`)} from ${fromTable} ${distinctCountWhereSQL}`;

  const { data: distinctValuesByFieldsMap, isLoading: isLoadingCounts } = useQuerySql({
    key: 'tab-table',
    sql: populatedFieldsQuery as string,
    debounceQueries: false,
    onSuccess: (d: any) =>
      Object.keys(d[0]).reduce((acc: any, key: string) => {
        if (key.includes(hasNonNullPrefix)) {
          return { ...acc, [key.replace(hasNonNullPrefix, '')]: d[0][key] };
        }
        return acc;
      }, {}),
    options: { enabled: enabled && !!ticketKey && shouldCountDistinctValues },
  });

  return {
    data,
    isLoading: isLoading || (!data && !errors?.length),
    isLoadingCounts,
    isRefetching,
    sql: `${allSQL} LIMIT ${ROW_LIMIT}`,
    exportSql,
    count: totals,
    whereSQL,
    distinctValuesByFieldsMap,
  };
};

export const useAllOutegrationsListByProjName = ({
  projName,
  selectFunc,
  enabled = true,
}: {
  projName: string;
  selectFunc: (data: Outegration[]) => any;
  enabled?: boolean;
}) => {
  const { userPermissions } = useAvContext();
  const hasPermission = hasPermissionToReadTicketOutegrations({
    projName,
    hasAllowedPermissionToResource: userPermissions?.hasAllowedPermissionToResource,
  });

  return useGqlQuery<Outegration[]>({
    query: ALL_ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME,
    queryKey: [QueryKey.outegrationsTicketsDropdown, 'All', projName],
    variables: { projectionName: projName },
    enabled: !!projName && enabled && hasPermission,
    select: selectFunc,
  });
};

export const OutegrationsListByProjectionName = (isWorkManagement, projectionName, enabled = false, isCMDB = false) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const { data, isLoading } = useGqlQuery<Outegration[]>({
    query: ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME,
    queryKey: [QueryKey.outegrationsTicketsDropdown, projectionName, enabled, isWorkManagement, isCMDB],
    variables: { projectionName },
    enabled:
      enabled &&
      (projectionName === entityProjectionNames.Incident ||
        hasAllowedPermissionToResource({
          resource: PermissionEntitiesNames.INT,
          category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
          permission: Permission.CREATE,
        }) ||
        hasAllowedPermissionToResource({
          resource: PermissionEntitiesNames.INT,
          category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
          permission: Permission.UPDATE,
        }) ||
        (projectionName === entityProjectionNames.Exception &&
          hasAllowedPermissionToResource({
            category: PermissionAppName.VULNERABILITIES,
            resource: PermissionEntitiesNames.EXCEPTION,
            permission: Permission.UPDATE,
          })) ||
        (projectionName === entityProjectionNames.Exception &&
          hasAllowedPermissionToResource({
            category: PermissionAppName.VULNERABILITIES,
            resource: PermissionEntitiesNames.EXCEPTION,
            permission: Permission.CREATE,
          }))),
  });

  const outegrations: Outegration[] = useMemo(
    () =>
      data?.filter(
        ({ category, active, integrationType }) =>
          (isCMDB
            ? category === TARGET_MATCHING_CATEGORY_ENUM.CMDB
            : isWorkManagement
              ? ![TARGET_MATCHING_CATEGORY_ENUM.WORK_MANAGEMENT, TARGET_MATCHING_CATEGORY_ENUM.CMDB].includes(category) ||
                isAnyTargetType(integrationType)
              : [
                  TARGET_MATCHING_CATEGORY_ENUM.WORK_MANAGEMENT,
                  TARGET_MATCHING_CATEGORY_ENUM.EXCEPTIONS,
                  TARGET_MATCHING_CATEGORY_ENUM.SCANNER,
                ].includes(category)) && !!active
      ) || [],
    [data]
  );
  return { outegrations, isLoading };
};

export function isAnyTargetType(integrationType) {
  return integrationType === 'ANYTARGET';
}

export const predefinedSavedViewFilters = ({ buckets, pathAlias, projectionName, userName }) => {
  const isTickets = projectionName === entityProjectionNames.Ticket;
  const now = new Date();
  const confirmedKey = 'closed';
  const notConfirmedStatuses = buckets?.filter(bucket => !bucket[confirmedKey]).flatMap(({ statuses }) => statuses.map(({ name }) => name));
  const confirmedStatuses = buckets?.filter(bucket => bucket[confirmedKey]).flatMap(({ statuses }) => statuses.map(({ name }) => name));
  return {
    active: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
      ...(isTickets && { [`${pathAlias}.state`]: ['ACTIVE'] }),
    },
    openTickets: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
    },
    pendingConfirmation: {
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
      [`${pathAlias}.state`]: [isTickets ? 'INACTIVE' : 'FULLY_REMEDIATED'],
    },
    overSla: {
      [`${pathAlias}.sla`]: [
        encodeURIComponent(JSON.stringify({ value: { from: new Date('2020/01/01').toISOString(), to: now.toISOString() } })),
      ],
      [`${pathAlias}.current_status.name`]: notConfirmedStatuses,
    },
    closedTickets: {
      [`${pathAlias}.current_status.name`]: confirmedStatuses,
      ...(isTickets && { [`${pathAlias}.state`]: ['ACTIVE', 'INACTIVE'] }),
    },
    missingAssignee: { [`${pathAlias}.assignee_id`]: [null] },
    allTickets: {},
    ticketsWithNoIntegration: {
      [`${pathAlias}.integration_info.key`]: [null],
    },
    newFindings: {
      [`finding.first_seen`]: [
        encodeURIComponent(JSON.stringify({ value: { from: sub(now, { days: 7 }).toISOString(), to: now.toISOString() } })),
      ],
    },
    approachingSla: {
      [`${pathAlias}.sla`]: [
        encodeURIComponent(
          JSON.stringify({
            value: {
              from: now.toISOString(),
              to: add(now, {
                days: 5,
              }).toISOString(),
            },
          })
        ),
      ],
    },
    activeByEntity: {
      [`${pathAlias}.state`]: ['ACTIVE'],
    },
    activeCriticalAndHigh: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.severity`]: [SeverityLabels.Critical, SeverityLabels.High],
    },
    coverage: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.type`]: ['Coverage'],
    },
    CMDBHygiene: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.type`]: ['CMDB Hygiene'],
    },
    newAndActive: {
      [`${pathAlias}.state`]: ['ACTIVE'],
      [`${pathAlias}.created`]: [
        encodeURIComponent(JSON.stringify({ value: { from: sub(now, { days: 7 }).toISOString(), to: now.toISOString() } })),
      ],
    },
    exceptionsReviewer: {
      'reviewer.name': [userName],
    },
    exceptionsRequester: {
      'requester.name': [userName],
    },
  };
};

export function useExportSqlToCsv(sql, headers, origin, exportData: unknown = undefined) {
  const { enqueueSnackbar } = useSnackbar();
  const [isWorking, setIsWorking] = useState<number | undefined>();
  const exportToCSV = data => {
    const filteredHeaders = headers.filter(h => !!(h.label || h.header));
    const csv = arrayToCsv([
      filteredHeaders.map(h => h.label || h.header),
      ...data.map(row =>
        filteredHeaders.reduce((acc, { id: key, getValue }) => {
          acc.push(getValue ? getValue(row) : typeof row[key] === 'object' ? JSON.stringify(row[key]) : row[key]);
          return acc;
        }, [])
      ),
    ]);
    return downloadBlob(csv, 'text/csv;charset=utf-8;', `export-${Date.now()}.csv`);
  };
  const { isLoading } = useQuerySql({
    origin,
    key: `${isWorking}-sql`,
    sql: isWorking ? sql : null,
    debounceQueries: false,
    options: { enabled: !exportData },
    onSuccess: d => {
      try {
        exportToCSV(d);
        setIsWorking(undefined);
      } catch (e) {
        enqueueSnackbar('Export failed', { variant: 'error' });
        console.error(e);
      }
    },
  });

  return { exportToCSV: () => (exportData ? exportToCSV(exportData) : setIsWorking(Date.now())), isLoading };
}

export function useExportQueryObjectToCsv(queryObject, headers, origin, globalFilters) {
  const { enqueueSnackbar } = useSnackbar();
  const [isWorking, setIsWorking] = useState(false);
  const exportToCSV = data => {
    const filteredHeaders =
      headers?.filter(h => !!h.label) ||
      Object.keys(data[0]).reduce<{ id: string; label: string }[]>(
        (acc, key) => (key !== 'id' ? [...acc, { id: key, label: key }] : acc),
        []
      );
    const csv = arrayToCsv([
      filteredHeaders.map(h => h.label),
      ...data.map(row =>
        filteredHeaders.reduce((acc, { id: key, getValue }) => {
          acc.push(getValue ? getValue(row) : row[key]);
          return acc;
        }, [])
      ),
    ]);
    return downloadBlob(csv, 'text/csv;charset=utf-8;', `export-${Date.now()}.csv`);
  };

  const { data, isLoading } = useQueryObject({
    origin,
    queryObject,
    options: { enabled: isWorking },
    contextFilter: globalFilters,
    onSuccess: data => {
      try {
        exportToCSV(data);
      } catch (e) {
        enqueueSnackbar('Export failed', { variant: 'error' });
        console.error(e);
      }
      setIsWorking(false);
      return data;
    },
  });
  useEffect(() => {
    if (data && isWorking) {
      exportToCSV(data);
      setIsWorking(false);
    }
  }, [isWorking]);

  return { exportToCSV: () => setIsWorking(true), isLoading };
}

export const TicketInfoContext = createContext<Record<string, any>>({});
export const useTicketInfo = () => useContext(TicketInfoContext);

export const buildOutegrationsQuery = outegrations =>
  outegrations
    ?.filter(
      ({ matchingConditionAsSql, targetMatchingType }) =>
        matchingConditionAsSql && targetMatchingType === TARGET_MATCHING_TYPE_ENUM.CONDITION
    )
    .map(alert => `IF(${alert?.matchingConditionAsSql}, true, false) as target_${alert?.id}`)
    .join(',');

export interface UserView {
  id: string;
  name: string;
  global: boolean;
  config: { [key: string]: any };
  accountDefaultView?: boolean;
  defaultView?: boolean;
  createdByUserId?: string;
}

export const useUserViews = ({
  screenType,
  proj,
  enabled = true,
}: {
  screenType: ScreenType | undefined;
  proj?: { name: string; builtIn: boolean };
  enabled?: boolean;
}) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const {
    data: userViews,
    isLoading,
    refetch,
  } = useGqlQuery<UserView[]>({
    queryKey: [QueryKey.Views, screenType, ...(proj ? [proj.name] : [])],
    query: SAVED_VIEWS,
    variables: { screenType, ...(proj && { projectionId: proj }) },
    enabled: enabled && !!screenType && hasAllowedPermissionToResource({ resource: PermissionEntitiesNames.VIEWS }),
  });

  return { userViews: userViews || [], isLoading, refetch };
};

/**
 * Applies the default saved view as soon as userViews finish loading, while
 * preserving any search/filter criteria the user has already entered.
 *
 * Why this is needed:
 * The saved-view effect in Tickets.tsx used to apply the default view only while
 * `isDefaultFilter` was still true. In practice the new AvFilters/AvTable render
 * work can push `userViews` resolution into the same React batch as a typed
 * search, so `isDefaultFilter` is already false by the time the views arrive and
 * the default Active view is never applied. That produces a `querySql` without
 * the Active-state filter and breaks the visual-explorer mock.
 *
 * This hook centralizes the default-view sync so Tickets.tsx stays focused and
 * other consumers can reuse the same behavior. It returns a `defaultViewApplied`
 * flag that callers can use to gate data queries until the view is resolved.
 */
export const useApplyDefaultSavedView = ({
  isLoadingViews,
  defaultFilter,
  defaultView,
  viewIdToViewInfo,
  filters,
  setFilters,
  entityTypeId,
  buckets,
  entityPathAlias,
  projectionName,
  userName,
  isViewSelectionCleared,
  hasInitialURLSearchParams,
}: {
  isLoadingViews: boolean;
  defaultFilter: Record<string, any>;
  defaultView: any;
  viewIdToViewInfo: Record<string, any>;
  filters: Record<string, any[]>;
  setFilters: (filters: Record<string, any>) => void;
  entityTypeId: string;
  buckets: any[];
  entityPathAlias: string;
  projectionName: string;
  userName: string;
  isViewSelectionCleared: boolean;
  hasInitialURLSearchParams: boolean;
}) => {
  const [defaultViewApplied, setDefaultViewApplied] = useState(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  const defaultFilterRef = useRef(defaultFilter);
  defaultFilterRef.current = defaultFilter;

  useEffect(() => {
    setDefaultViewApplied(false);
  }, [entityTypeId]);

  useEffect(() => {
    if (isLoadingViews) {
      setDefaultViewApplied(false);
      return;
    }
    const currentFilters = filtersRef.current;

    if (isViewSelectionCleared) {
      setDefaultViewApplied(true);
      return;
    }

    // A viewId in the URL means a specific view was shared/bookmarked. If the
    // non-view filters are still the default, expand the view config now so the
    // first query uses the saved view's filters.
    if (currentFilters.viewId?.length) {
      const { viewId, ...otherFilters } = currentFilters;
      const isDefaultFilterWithoutView = isDeepEqual(
        { ...defaultFilterRef.current, ticketCategory: currentFilters.ticketCategory },
        otherFilters
      );
      if (isDefaultFilterWithoutView && viewIdToViewInfo[viewId[0]]) {
        const selectedView = viewIdToViewInfo[viewId[0]];
        const { id, config } = selectedView;
        const predefinedFilters = predefinedSavedViewFilters({
          buckets,
          pathAlias: entityPathAlias,
          projectionName,
          userName,
        })[config?.tab?.[0]];
        setFiltersRef.current({
          ...(config?.tab?.length ? { ...config, ...predefinedFilters } : config),
          viewId: [id],
          ...(currentFilters.ticketCategory?.length ? { ticketCategory: currentFilters.ticketCategory } : {}),
        });
      }
      setDefaultViewApplied(true);
      return;
    }

    if (hasInitialURLSearchParams) {
      setDefaultViewApplied(true);
      return;
    }

    // No viewId and views are loaded: apply the default view while preserving
    // any search/filter criteria the user has already entered.
    if (!defaultView?.id || !Object.keys(viewIdToViewInfo).length) {
      setDefaultViewApplied(true);
      return;
    }

    const { id, config } = defaultView;
    const predefinedFilters = predefinedSavedViewFilters({
      buckets,
      pathAlias: entityPathAlias,
      projectionName,
      userName,
    })[config?.tab?.[0]];

    setFiltersRef.current({
      ...(config?.tab?.length ? { ...config, ...predefinedFilters } : config),
      viewId: [id],
      ...(currentFilters.search?.length ? { search: currentFilters.search } : {}),
      ...(currentFilters.ticketCategory?.length ? { ticketCategory: currentFilters.ticketCategory } : {}),
      ...(currentFilters.headCells?.length && !config.headCells ? { headCells: currentFilters.headCells } : {}),
    });
    setDefaultViewApplied(true);
  }, [
    isLoadingViews,
    defaultView,
    viewIdToViewInfo,
    entityTypeId,
    buckets,
    entityPathAlias,
    projectionName,
    userName,
    isViewSelectionCleared,
    hasInitialURLSearchParams,
    setFilters,
    defaultFilter,
  ]);

  return defaultViewApplied;
};

export const getHeadCellsWithSort = (headCells, orderBy) =>
  headCells.map(h => {
    const orderByItem = orderBy.find(o => o.property === h.id);
    const sort = orderByItem ? (orderByItem.isAsc ? 'asc' : 'desc') : undefined;
    return { ...h, sort };
  });

export const useBulkIntegration = (projectionId, selectedKeys, onSuccess) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: outegrationId =>
      api(apiUrls.UPDATE_INTEGRATION, {
        options: { method: 'PUT' },
        isWebserver: false,
        body: { keys: selectedKeys, alertDestinationId: outegrationId, projectionId },
        onSuccess: onSuccess(outegrationId),
      }),
  });
};

export const useBulkSinglesIntegration = ({ onSuccess }) => {
  const { api } = useAvContext();
  return useMutation({
    mutationFn: ({ request }: { request: IntegrationSingleTM }) =>
      api(CREATE_BULK_SINGLES_INTEGRATION, {
        options: { request },
        onSuccess: ({ data }) => {
          onSuccess('')(data.bulkCreateIntegration);
        },
      }),
  });
};

const RELATED_TICKETS_BATCH_SIZE = 500;

export const toKeyBatches = (data: any[], field: string): string[] => {
  const escaped = data.map(row => row[field] && `'${getEscapedValue(row[field])}'`).filter(Boolean);
  return Array.from({ length: Math.ceil(escaped.length / RELATED_TICKETS_BATCH_SIZE) }, (_, i) =>
    escaped.slice(i * RELATED_TICKETS_BATCH_SIZE, (i + 1) * RELATED_TICKETS_BATCH_SIZE).join(',')
  );
};

export const useGetRelatedTickets = ({ ticket, headCells }) => {
  const { featureFlags } = useAvContext();
  const { entityType, projectionId, ticketKey, entityPathAlias } = ticket;
  const entityConfig = useCurrentEntityViewConfig(entityType)!;
  const { groupingKeyField } = entityConfig;
  const { data: batchSQLs, isLoading } = useQuerySql({
    key: 'grouping_keys',
    sql: `SELECT DISTINCT ${groupingKeyField} FROM ${projectionId.name} WHERE ${entityPathAlias}._key = '${ticketKey}'`,
    options: { enabled: !!groupingKeyField },
    debounceQueries: false,
    onSuccess: data =>
      toKeyBatches(data, groupingKeyField as string).map(batch =>
        entityConfig.getBaseSql!({
          fields: headCells,
          where: `WHERE ${groupingKeyField} in (${batch}) AND ${entityPathAlias}._key != '${ticketKey}'`,
          isNewRiskScoreEnabled: featureFlags[FeatureFlags.NewRiskScore],
        })
      ),
  });

  const batchDataRef = useRef<Record<number, any[]>>({});
  const prevBatchSQLsKeyRef = useRef('');
  const batchSQLsKey = batchSQLs?.join('||') ?? '';
  if (prevBatchSQLsKeyRef.current !== batchSQLsKey) {
    prevBatchSQLsKeyRef.current = batchSQLsKey;
    batchDataRef.current = {};
  }

  const { isLoading: isLoadingBatches, errors: batchErrors } = useQuerySql({
    key: 'related_tickets',
    sql: batchSQLs?.length ? batchSQLs : '',
    debounceQueries: false,
    options: { enabled: !!(batchSQLs?.length && groupingKeyField) },
    onSuccess: (data, index) => {
      batchDataRef.current[index] = data;
      return data;
    },
  });

  const hasBatchErrors = !!batchErrors?.length;

  const flatData = useMemo(
    () =>
      batchSQLs?.length && !isLoadingBatches && !hasBatchErrors ? batchSQLs.flatMap((_, i) => batchDataRef.current[i] ?? []) : undefined,
    [batchSQLs, isLoadingBatches, hasBatchErrors]
  );

  return { data: flatData, isLoading: isLoading || isLoadingBatches };
};

const getGroupByActionsMap = (projectionName: string) => ({
  [groupByKeys.assignee?.[projectionName]]: { sort: groupByKeys.assignee.sort },
  [groupByKeys.severity?.[projectionName]]: { sort: groupByKeys.severity.sort, format: groupByKeys.severity.formatter },
  [groupByKeys.sla?.[projectionName]]: { sort: groupByKeys.sla.sort, format: groupByKeys.sla.formatter },
  [groupByKeys.firstSeen?.[projectionName]]: { sort: groupByKeys.firstSeen.sort, format: groupByKeys.firstSeen.formatter },
  [groupByKeys.avalorStatus?.[projectionName]]: { sort: groupByKeys.avalorStatus.sort, format: groupByKeys.avalorStatus.formatter },
  [groupByKeys.created?.[projectionName]]: { sort: groupByKeys.created.sort, format: groupByKeys.created.formatter },
  [groupByKeys.type?.[projectionName]]: { sort: groupByKeys.type.sort },
  'exception.status': {
    sort: ({ title: a }, { title: b }) => (a === 'Requested' ? -1 : b === 'Requested' ? 1 : a.localeCompare(b)),
  },
});

export const LIMIT_GROUP_BY_VALUES = 40000;
export const useGetGroupBy = ({
  groupByKey,
  entityName,
  projectionName,
  projections,
  where,
  filters = {},
  onSuccess,
  displayNameMapFormatter,
  enabled = false,
}: {
  groupByKey: string;
  entityName: string;
  projectionName: string;
  projections: AccountEntities;
  where: string;
  filters?: Record<string, any>;
  onSuccess?: (options: any[]) => void;
  displayNameMapFormatter?: { [key: string]: (value: any) => string };
  enabled?: boolean;
}) => {
  const {
    measurements,
    accountEntities: { fieldMap },
  } = useAvContext();

  const hasActiveMetricFilters = measurements?.visible.some(({ systemName }) => !!filters[systemName]);
  const metricParam = hasActiveMetricFilters ? 'AVALOR PARAM metric_filter_root_projection = true; ' : '';

  const isKeyDateField = [FieldType.DateTime, FieldType.Date].includes(fieldMap[groupByKey]?.type as any);
  const groupByValuesSql = sqlFormatter(
    `${metricParam}SELECT ${
      groupByKeysQueryProjection(projectionName)[groupByKey] || `${groupByKey} as val`
    }, COUNT_DISTINCT(${entityName}._key) as count from ${projectionName} ${where} GROUP BY val ORDER BY val ASC${
      isKeyDateField ? ' LIMIT 1000' : ` LIMIT ${LIMIT_GROUP_BY_VALUES}`
    }`
  );
  const { data, refetch, isLoading } = useQuerySql({
    key: 'group_by_values',
    sql: groupByValuesSql,
    debounceQueries: false,
    options: { enabled: !!groupByKey && enabled },
    onSuccess: groupByValues => {
      const groupByKeyParts = groupByKey?.split('.');
      const keyToRetrieve = groupByKeyParts && groupByKeyParts.length > 1 ? groupByKeyParts.slice(1).join('.') : groupByKey;
      const groupByKeyField =
        projections[projectionName].nameMap[keyToRetrieve] ||
        projections[projectionName].fieldList.INTERACTIVE.find(({ pathAlias }) => pathAlias === keyToRetrieve)?.title;
      const optionsMap = groupByValues
        ? groupByValues.reduce((acc, row) => {
            const keys = Array.isArray(row.val) ? row.val : [row.val];
            keys.forEach(rawKey => {
              const title = `${groupByKeyField}: ${
                getGroupByActionsMap(projectionName)?.[groupByKey]?.format?.(rawKey) ||
                displayNameMapFormatter?.[groupByKey]?.(rawKey) ||
                rawKey
              }`;

              acc[rawKey] = { title, value: rawKey, count: (acc[rawKey]?.count || 0) + row.count };
            });
            return acc;
          }, {})
        : {};
      const options = Object.values(optionsMap);
      options.sort(getGroupByActionsMap(projectionName)?.[groupByKey]?.sort);
      onSuccess?.(options);
      return options;
    },
  });
  return { data, refetch, isLoading };
};

export const useGetShouldShowTabs = ({
  tabs = [],
  ticketKey,
  projectionName,
  entityPathAlias,
}: {
  tabs: TTab[];
  ticketKey: string;
  projectionName: string;
  entityPathAlias: string;
}) => {
  const dims = tabs.reduce<{ name: string; alias: string }[]>((acc, { name, shouldShowTabField }) => {
    if (shouldShowTabField) {
      const nameWithoutSpaces = name.replaceAll(' ', '');
      const fieldPathAlias = shouldShowTabField.split('.')[0];
      const fieldExpression = `IF(COUNT_DISTINCT_IF(${fieldPathAlias}._key, ${shouldShowTabField} != null) > 0, true, false)`;
      return [...acc, { name: fieldExpression, alias: nameWithoutSpaces }];
    }
    return acc;
  }, []);

  return useQueryObject({
    queryObject: {
      select: { dims, metrics: [] },
      projectionId: { name: projectionName, builtIn: true },
      filter: field(`${entityPathAlias}._key`).str.equals(getEscapedValue(ticketKey)),
      sorting: [],
    },
    onSuccess: data => data?.[0] || {},
    options: {
      enabled: !!dims.length && !!ticketKey,
    },
  });
};

export const useGetSourcesValues = ({ projectionId, fieldName, entityPathAlias, avalorKey, field, showAll, projAliasPath }) => {
  const defaultValue = { sources: [], totalCount: 0 };

  const { data: { sources, totalCount } = defaultValue, isLoading } = useQueryObject({
    onSuccess: d =>
      d?.[0]
        ? {
            ...d?.[0],
            sources: d?.[0].sources
              .map(({ value, items }) => ({ value, source: items[0] }))
              .sort((a, b) => a.source.localeCompare(b.source)),
          }
        : defaultValue,
    options: { enabled: !!field.ingressSources && !!avalorKey, placeholderData: keepPreviousData },
    queryObject: generateSourcesValuesQuery({
      projectionId,
      fieldName,
      entityPathAlias,
      projAliasPath,
      avalorKey,
      sources: field.ingressSources,
      showAll,
    }),
  });
  return { sources, totalCount, isLoading };
};

const getInstantLoadingMessage = () => [{ id: `${Date.now()}`, type: MessageStatus.LOADING, header: '', message: 'Loading' }];
export const useSplitEntities = ({ onSuccess = d => d }) => {
  const { api } = useAvContext();
  const { setInstantMessages, setMessageList } = useContext(NotificationContext);
  const instantMessage = getInstantLoadingMessage();

  return useMutation({
    mutationFn: ({
      name,
      sourceProjectionID,
      sourceProjectionKeys,
      splitStrategy = 'ALL',
      queryWhereClause = null,
      operation = 'SPLIT',
      overrideFields,
      keepOriginalAgg = false,
    }: {
      name: string;
      sourceProjectionID: EntityTypeID;
      sourceProjectionKeys?: string[];
      splitStrategy?: string;
      queryWhereClause?: null | string;
      operation?: string;
      overrideFields?: Record<string, any>;
      keepOriginalAgg?: boolean;
    }) => {
      const removeLoadingMessage = () => setMessageList(prev => prev.filter(message => message.id !== instantMessage[0].id));
      setInstantMessages(instantMessage);
      return api(SPLIT_ENTITIES, {
        options: {
          splitEntitiesRequest: {
            entityName: name,
            sourceProjectionID,
            sourceProjectionKeys,
            splitStrategy,
            queryWhereClause,
            operation,
            keepOriginalAgg,
            ...(overrideFields ? { overrideFields } : {}),
          },
        },
        onSuccess: ({ data }) => {
          removeLoadingMessage();
          return onSuccess(data.entitiesSplit);
        },
      }).catch(error => {
        removeLoadingMessage();
        throw error;
      });
    },
  });
};

export const useMergeEntities = ({ onSuccess = d => d }) => {
  const { api } = useAvContext();
  const { setInstantMessages, setMessageList } = useContext(NotificationContext);
  const instantMessage = getInstantLoadingMessage();
  return useMutation({
    mutationFn: ({
      title,
      keys,
      projectionId,
      overrideFields = {},
    }: {
      title: string;
      keys: string[];
      projectionId: EntityTypeID;
      overrideFields: Record<string, any>;
    }) => {
      setInstantMessages(instantMessage);
      return api(MERGE_ENTITIES, {
        options: {
          mergeEntitiesRequest: {
            keys,
            projectionID: projectionId,
            overrideFields: {
              title,
              ...overrideFields,
            },
          },
        },
        onSuccess: ({ data }) => {
          setMessageList(prev => prev.filter(message => message.id !== instantMessage[0].id));
          return onSuccess(data.entitiesMerge);
        },
      });
    },
  });
};

export const useGetEntityActions = (
  projectionName,
  ignorePermissions = false,
  shouldAllowAction?: (action: string, app: PermissionCategoriesNames) => boolean
) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
  } = useAvContext();
  const { pathname } = useLocation();
  const matches = useMatch({ strict: false });
  const routeShouldAllowAction = (matches?.context as any)?.shouldAllowAction;
  const app = appToPermissionCategory[getAppIdFromPathName(pathname)];
  const entityConfig = entityViewConfig[projectionName as EntityProjectionName];
  const actionsMap = Object.values(entityConfig?.actions || {}).reduce<Record<string, boolean>>(
    (acc, curr) => ({ ...acc, ...(curr || {}) }),
    {}
  );
  return Object.keys(actionsMap).reduce((acc, act) => {
    if (shouldAllowAction && !shouldAllowAction(act, app)) {
      return { ...acc, [act]: false };
    }
    if (routeShouldAllowAction && !routeShouldAllowAction(projectionName, act, app)) {
      return { ...acc, [act]: false };
    }
    if (
      ignorePermissions
        ? !!actionsMap[act]
        : !!actionsMap[act] &&
          (getActionsPermissionConfig(projectionName, app)[act]
            ? hasAllowedPermissionToResource({
                category: projectionNameToCategoryName[projectionName] as PermissionCategoriesNames,
                ...getActionsPermissionConfig(projectionName, app)[act],
              })
            : true)
    ) {
      return { ...acc, [act]: true };
    }
    return { ...acc, [act]: false };
  }, {});
};

export const useGetAllFields = ({ allFields, entityPathAlias, ticketKey, projectionId, showAllFields, showAllFieldsAsList }) => {
  const queryObject: DwQueryRequest = {
    select: {
      dims: allFields.map(v => ({ name: v.value })),
      metrics: [],
    },
    filter: {
      and: {
        operands: [
          {
            expression: {
              fieldName: `${entityPathAlias}._key`,
              stringCondition: {
                equals: ticketKey,
              },
            },
          },
        ],
      },
    },
    projectionId,
    sorting: [],
  };
  const {
    data: allFieldsData = emptyObject,
    isLoading,
    isRefetching,
  } = useQueryObject({
    queryObject,
    onSuccess: d => (showAllFieldsAsList ? d : d[0]),
    options: { enabled: showAllFields && !!ticketKey },
  });
  const isLoadingAllFieldsData = isLoading || isRefetching;
  return { allFieldsData, isLoadingAllFieldsData };
};

export const useGetTicketMetricsData = ({
  projName,
  entityPathAlias,
  ticketKey,
}: {
  projName: string;
  entityPathAlias: string;
  ticketKey?: string | number;
}) => {
  const { featureFlags } = useAvContext();
  const metrics = getMetricsByProjName(projName as EntityProjectionName, featureFlags[FeatureFlags.SOCWIncidentsTabsCount]);
  const { data: metricsData, isLoading: isLoadingMetrics } = useQueryObject({
    queryObject: {
      select: {
        metrics,
        dims: [],
      },
      filter: field(`${entityPathAlias}._key`).str.equals(`${ticketKey}`),
      projectionId: { name: projName, builtIn: true },
      sorting: [],
    },
    options: { enabled: !!ticketKey && !!metrics?.length },
  });

  return { ticketMetricsData: metricsData?.[0], isLoading: isLoadingMetrics };
};

const getFieldPrefix = (fieldPath: string) => fieldPath?.split('.')[0];

const isRelatedEntityField = (fieldPath: string, entityPathAlias: string) => {
  if (!fieldPath || !entityPathAlias) return false;
  const prefix = getFieldPrefix(fieldPath);
  return Boolean(prefix) && prefix !== entityPathAlias;
};

export const useGetIsManualOverrideEnabled = ({ ticket, tabType }) => {
  const {
    userPermissions: { hasAllowedPermissionToResource },
    accountEntities: { fieldMap },
  } = useAvContext();
  const { data: { fieldRegulations } = emptyObject } = useFieldRegulationsData();
  const projectionName = ticket.projectionId.name;

  return (fieldName = '') => {
    const activeEntityPathAlias = ticket.entityPathAlias;
    const isFindingVulnerabilityTab = ticket.projectionId.name === entityProjectionNames.Finding && tabType === TabTypeEnum.VULNERABILITY;

    if (isRelatedEntityField(fieldName, activeEntityPathAlias) && !isFindingVulnerabilityTab) {
      return false;
    }

    const fieldProjection = fieldMap[fieldName]?.mainProjId?.name;
    const manualOverrideBaseCondition = fieldName =>
      hasAllowedPermissionToResource({
        category: projectionNameToCategoryName[fieldProjection || projectionName] as PermissionCategoriesNames,
        ...getActionsPermissionConfig(fieldProjection || projectionName)[ACTIONS_MAP.UPDATE],
      }) && getIsManualOverrideEnabled(getFieldName(fieldName), fieldProjection || projectionName, fieldRegulations);
    return isFindingVulnerabilityTab
      ? manualOverrideBaseCondition(getFieldName(fieldName))
      : ticket.activeTabProjection === fieldProjection && manualOverrideBaseCondition(getFieldName(fieldName));
  };
};

export const useGetFilteredFieldsList = ({
  sourceArray,
  allTicketFieldsData,
  isLoadingAllFieldsData,
  targetArrayOfArrays,
  search,
  ticket,
  tabType,
  showAllFields,
}: {
  sourceArray: FieldListObject[];
  isLoadingAllFieldsData: boolean;
  showAllFields: boolean;
  allTicketFieldsData: Record<string, any>;
  targetArrayOfArrays: any[][];
  search: string;
  ticket: any;
  tabType: string;
}) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();

  const isManualOverrideEnabled = useGetIsManualOverrideEnabled({ ticket, tabType });

  const projection = ticket.projectionId.name;
  const activeEntityPathAlias = ticket.entityPathAlias;

  const allFields = useMemo(
    () =>
      sourceArray.map(v => {
        const isMainField = v.mainProjId.name === projection && !isRelatedEntityField(v.value, activeEntityPathAlias);

        const displayName =
          isMainField || (v.title && v.title !== v.displayName)
            ? isMainField
              ? v.displayName
              : v.title
            : (() => {
                const prefix = getFieldPrefix(v.value);
                if (!prefix) return v.displayName;
                const capitalized = formatStartEachWordWithCapitalLetter(prefix);
                return `${capitalized} ${v.displayName}`;
              })();

        return {
          field: v.value,
          displayName,
          isLoading: isLoadingAllFieldsData,
          isAdditionalField: true,
          group: v.group,
        };
      }),
    [sourceArray, projection, isLoadingAllFieldsData, showAllFields, activeEntityPathAlias]
  );

  const showAllFieldsList = [...targetArrayOfArrays.flat(), ...allFields]
    .map(v => {
      const fieldType = aggProjs[projection].typeMap[v.field];
      const isDate = [FieldType.DateTime, FieldType.Date].includes(fieldType as string);
      const fieldValue = isDate
        ? allTicketFieldsData[v.field]
          ? formatAsUTC(allTicketFieldsData[v.field], false, true)
          : 'Not Set'
        : v.showNull && !allTicketFieldsData[v.field]
          ? 'Empty'
          : allTicketFieldsData[v.field];

      const needsPrefix = isRelatedEntityField(v.field, activeEntityPathAlias) && !v.isAdditionalField && v.displayName;

      const displayName = needsPrefix ? `${formatStartEachWordWithCapitalLetter(getFieldPrefix(v.field))} ${v.displayName}` : v.displayName;

      return {
        ...v,
        displayName,
        isManualOverrideEnabled: isManualOverrideEnabled(v.field),
        fieldType,
        fieldValue,
      };
    })
    .filter(v => {
      const lowerSearch = search.toLowerCase();
      const matchesSearch =
        v.displayName.toLowerCase().includes(lowerSearch) || JSON.stringify(v.fieldValue)?.toLowerCase()?.includes(lowerSearch);
      const shouldShow = !v.isAdditionalField || v.isManualOverrideEnabled || v.showNull || !isNullOrUndefined(v.fieldValue) || v.isLoading;
      return matchesSearch && shouldShow;
    })
    .sort((a, b) => (!a.group ? -1 : !b.group ? 1 : (a.group || '').localeCompare(b.group || '')))
    .reduce(
      (acc, field, index) => {
        const bucketIndex = index % 2;
        if (bucketIndex === 0) {
          return [[...acc[bucketIndex], field], acc[1]];
        }
        return [acc[0], [...acc[bucketIndex], field]];
      },
      [[], []] as ConfigField[][]
    );

  const showDefaultFields = targetArrayOfArrays.map(array =>
    array.map(v => ({
      ...v,
      isManualOverrideEnabled: isManualOverrideEnabled(v.field),
      fieldType: aggProjs[projection].typeMap[v.field],
    }))
  );

  return showAllFields ? showAllFieldsList : showDefaultFields;
};

const SPLIT_ENTITIES = gql`
  mutation entitiesSplit($splitEntitiesRequest: SplitEntitiesRequest!) {
    entitiesSplit(splitEntitiesRequest: $splitEntitiesRequest) {
      wfId
      runId
    }
  }
`;

const MERGE_ENTITIES = gql`
  mutation entitiesMerge($mergeEntitiesRequest: MergeEntitiesRequest!) {
    entitiesMerge(mergeEntitiesRequest: $mergeEntitiesRequest) {
      wfId
      runId
    }
  }
`;

const SAVED_VIEWS = gql`
  query findViewsByUserIdAndAccountIdAndScreenType($screenType: ScreenType!, $projectionId: ProjectionIDInput) {
    findViewsByUserIdAndAccountIdAndScreenType(screenType: $screenType, projectionId: $projectionId) {
      id
      name
      screenType
      config
      publicView
      createdByUserId
      createdAt
      favorite
      defaultView
      global
      accountDefaultView
    }
  }
`;

const GET_EXCEPTION_RULES_FILTER = gql`
  query getExceptionRulesFilter($entityKey: String!) {
    getExceptionRulesFilter(entityKey: $entityKey) {
      scope
      filter
    }
  }
`;

export const useExceptionRulesFilter = ({ entityKey, projectionId }: { entityKey: string; projectionId: string }) => {
  const {
    data: exceptionRules,
    isLoading,
    error,
  } = useGqlQuery<ExceptionRulesFilterResponse>({
    query: GET_EXCEPTION_RULES_FILTER,
    queryKey: [QueryKey.ExceptionRuleFilter, entityKey, projectionId],
    variables: { entityKey },
    enabled: !!entityKey,
  });

  const convertedFilter = useMemo(() => {
    if (!exceptionRules?.filter || exceptionRules.scope !== 'RULE' || isEmptyObject(exceptionRules.filter)) {
      return null;
    }
    const isProperFilter = exceptionRules.filter && ('and' in exceptionRules.filter || 'or' in exceptionRules.filter);
    return isProperFilter ? exceptionRules.filter : and(exceptionRules.filter);
  }, [exceptionRules?.scope, exceptionRules?.filter]);

  return { exceptionRules: convertedFilter, isLoading, error };
};

const ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME = gql`
  query findIntegrationInstanceByAccountIdAndProjectionName($projectionName: String!) {
    findIntegrationInstanceByAccountIdAndProjectionName(projectionName: $projectionName) {
      id
      name
      active
      integrationType
      matchingConditionAsSql
      targetMatchingType
      category
      redactedConfig
      projectionName
      redactedAuthConfig
      mapping {
        createMapping
        mappingProjection
      }
    }
  }
`;

const CREATE_BULK_SINGLES_INTEGRATION = gql`
  mutation bulkCreateIntegration($request: BulkCreateIntegrationRequestInput!) {
    bulkCreateIntegration(request: $request) {
      wfId
      runId
    }
  }
`;

export const ALL_ALERT_DESTINATIONS_LIST_BY_PROJECTION_NAME = gql`
  query findIntegrationInstanceByAccountIdAndProjectionName($projectionName: String!) {
    findIntegrationInstanceByAccountIdAndProjectionName(projectionName: $projectionName) {
      id
      name
      active
      integrationType
      matchingConditionAsSql
      targetMatchingType
      category
      redactedConfig
      projectionName
      redactedAuthConfig
      mapping {
        createMapping
        mappingProjection
      }
    }
  }
`;
